<template>
  <div>
    <vx-card id="camera-view" class="mb-base overflow-hidden" v-on="$listeners">
      <template slot="no-body">
        <div id="camera-center-area" class="vs-con-loading__container">
          <!-- 上部エリア: タイトル、アクションボタンなど -->
          <div
            class="flex w-full justify-between items-center"
            style="height: 3rem"
          >
            <div class="flex items-center w-full h-full forward-layer">
              <feather-icon
                icon="GridIcon"
                svgClasses="h-9 w-9"
                class="ml-2 mr-4 my-0 cursor-pointer"
                style="color: #48a0f4d0"
                @click="toListClicked"
              />

              <div v-if="camera" class="flex items-center">
                {{ camera.name }} ({{ camera.description }})

                <template
                  v-if="
                    mode === 'history' &&
                    camera &&
                    [
                      CAMERA_MOVIE_TYPE.HLS_CAMERA,
                      CAMERA_MOVIE_TYPE.SAFIE,
                    ].includes(camera.movieType)
                  "
                >
                  <!-- HLS_CAMERAのhistoryの場合、再生中のhourを表示 -->
                  <span class="mx-4 my-0"> >> </span>
                  {{ targetDateYmdSlash }}
                  <span class="mx-4 my-0"> >> </span>
                  <feather-icon
                    icon="ClockIcon"
                    svgClasses="h-5 w-5"
                    class="mr-2 my-0"
                  />
                  {{ targetHour }}
                </template>
              </div>
            </div>

            <div
              v-if="
                camera &&
                camera.movieType !== CAMERA_MOVIE_TYPE.RADAR &&
                camera.movieType !== CAMERA_MOVIE_TYPE.HLS_RADAR
              "
              class="flex justify-end h-full items-center forward-layer"
            >
              <!-- ONVIFボタン -->
              <div v-if="mode !== 'history' && onvifEnable">
                <div
                  @click="onvifActive = !onvifActive"
                  class="flex justify-center items-center cursor-pointer py-1 px-4 onvif-toggle-button"
                >
                  <div class="flex items-center">
                    <div class="material-icons mr-1" style="font-size: 18px">
                      control_camera
                    </div>
                    <div style="padding-top: 3px; font-size: smaller">
                      ONVIF
                    </div>
                  </div>
                </div>
              </div>

              <!-- カレンダー: history && HLS_CAMERA のみ  -->
              <div
                v-if="
                  mode === 'history' &&
                  camera &&
                  [
                    CAMERA_MOVIE_TYPE.HLS_CAMERA,
                    CAMERA_MOVIE_TYPE.SAFIE,
                  ].includes(camera.movieType)
                "
                class="flex justify-end items-center"
                style="width: 300px"
              >
                <div
                  class="flex items-center m-0"
                  style="border-radius: 4px"
                  :style="{
                    'background-color': canPrevDate ? '#48A0F414' : '#BDBDBD14',
                  }"
                >
                  <feather-icon
                    icon="ChevronLeftIcon"
                    @click="canPrevDate && incrementTargetDate(-1)"
                    svgClasses="h-9 w-9"
                    class="cursor-pointer"
                    :style="{ color: canPrevDate ? '#48A0F4' : '#BDBDBD' }"
                  />
                </div>

                <feather-icon
                  icon="CalendarIcon"
                  svgClasses="h-4 w-4"
                  class="calendar-icon"
                  style="
                    color: #48a0f4;
                    transform: translate(1.8rem, 0.1rem);
                    z-index: 9;
                  "
                />

                <div class="date-picker-container">
                  <flat-pickr
                    v-model="targetDateYmd"
                    :placeholder="targetDateYmdSlash"
                    :config.sync="configDayPicker"
                    class="camera-view-flat-pickr"
                  />
                </div>

                <div
                  class="flex items-center ml-5 mr-8 my-0 rounded"
                  :style="{
                    'background-color': canNextDate ? '#48A0F414' : '#BDBDBD14',
                  }"
                >
                  <feather-icon
                    icon="ChevronRightIcon"
                    svgClasses="h-9 w-9"
                    class="cursor-pointer"
                    @click="canNextDate && incrementTargetDate(1)"
                    :style="{
                      'pointer-events': canNextDate ? 'auto' : 'none',
                      color: canNextDate ? '#48A0F4' : '#BDBDBD',
                    }"
                  />
                </div>
              </div>

              <div
                v-if="mode !== 'history' || canRealtime"
                class="flex justify-end items-center"
                :style="{ width: mode === 'history' ? '9rem' : '5.2rem' }"
              >
                <vs-button
                  style="padding: 6px 0.8rem; margin-top: 2px; height: 30px"
                  type="border"
                  color="#48A0F4"
                  @click="changeMode"
                  icon-pack="feather"
                  :icon="mode === 'history' ? 'icon-clock' : 'icon-calendar'"
                >
                  <div style="font-size: smaller">
                    {{ mode === "history" ? "リアルタイム" : "履歴" }}
                  </div>
                </vs-button>
              </div>
            </div>
          </div>

          <!-- 中央エリア: 画像とコントロールボタン -->
          <div
            :style="{ height: `calc(100vh - ${videoPlayerAreaHeightOffset})` }"
          >
            <div
              v-if="
                !(
                  mode === 'history' &&
                  camera &&
                  camera.movieType === CAMERA_MOVIE_TYPE.CAMERA
                )
              "
            >
              <div class="flex w-full justify-between items-center">
                <div
                  class="live-camera-area"
                  :style="{ width: `calc(100% - ${onvifAreaSize.x})` }"
                >
                  <P>{{ cameraMsg }}</P>
                  <vs-button
                    v-if="hasVideoError"
                    icon-pack="feather"
                    icon="icon-rotate-cw"
                    color="rgb(255, 255, 255)"
                    type="line"
                    size="small"
                    @click="connect"
                    >{{ retryText }}</vs-button
                  >

                  <div
                    v-if="camera && !isHykeCam"
                    id="video-player-area"
                    @fullscreenchange="onFullscreenChange"
                    class="flex w-full justify-center items-center"
                    :style="{
                      height: `calc(100vh - ${videoPlayerAreaHeightOffset} - ${onvifAreaSize.y})`,
                    }"
                    style="background-color: black"
                  >
                    <!-- カメラ表示エリア -->
                    <div
                      class="flex w-full justify-center items-center"
                      :style="{ width: videoPlayerWidthByHeight }"
                      @click="onVideoClick"
                    >
                      <video-player
                        id="live-video-player"
                        class="media-video-player card-video vjs-big-play-centered"
                        ref="videoPlayer"
                        :options="playerOptions"
                        :style="{
                          'min-width': zoomSize,
                          'min-height': zoomSize,
                          'margin-bottom':
                            camera.movieType === CAMERA_MOVIE_TYPE.SAFIE &&
                            mode === 'history'
                              ? '40px'
                              : '0',
                        }"
                        @ready="onPlayerReadied"
                        @play="onPlayerPlay"
                        @pause="onPlayerPause"
                        @error="onPlayerError"
                        @ended="onPlayerEnded"
                      />

                      <!-- コントロールバーエリア -->
                      <div
                        class="w-full m-0 p-0"
                        style="position: absolute; bottom: 0; right: 0"
                        :style="{
                          display:
                            camera && onvifEnable && onvifActive
                              ? 'none'
                              : 'block',
                        }"
                      >
                        <camera-player-control
                          ref="playBackControls"
                          :current="currentMomentValueOf"
                          :start="playStartMomentValueOf"
                          :mode="mode"
                          :fps="fps"
                          :thumbnails="thumbnails"
                          @onPlay="onPlay"
                          @onChangeVolume="onChangeVolume"
                          @setFPS="setFPS"
                          @onChangeSeek="onChangeSeek"
                          @onChangeFullscreen="setFullscreen"
                        />
                      </div>
                      <!-- LIVEマーク: リアルタイムのみ -->
                      <div v-if="mode !== 'history'">
                        <div
                          class="flex"
                          style="
                            position: absolute;
                            bottom: 0;
                            left: 200px;
                            color: white;
                            background-color: #82828200;
                            padding: 0.4rem 0.6rem 0.8rem 0.6rem;
                          "
                          :style="{
                            display:
                              camera && onvifEnable && onvifActive
                                ? 'none'
                                : 'block',
                          }"
                        >
                          <span style="color: white; margin-left: 1rem"
                            >LIVE</span
                          >
                        </div>
                      </div>
                    </div>

                    <!-- Zoomボタン: 履歴 のみ -->
                    <div v-if="mode === 'history'">
                      <div
                        class="flex"
                        style="
                          position: absolute;
                          bottom: 0px;
                          left: 280px;
                          color: white;
                          background-color: #82828200;
                          padding: 0.4rem 0.6rem 0.8rem 0.6rem;
                        "
                      >
                        <feather-icon
                          icon="ZoomOutIcon"
                          svgClasses="h-6 w-6"
                          style="color: white; cursor: pointer"
                          @click="zoomClicked('minus')"
                        />
                        <div
                          v-for="(zoomValue, index) in zoomList"
                          :key="index"
                          @click="zoomClicked(zoomValue)"
                          :style="{
                            color:
                              targetZoom === zoomValue ? '#48A0F4' : 'white',
                            margin: '0 0.1rem',
                          }"
                        >
                          {{ targetZoom === zoomValue ? "●" : "○" }}
                        </div>
                        <feather-icon
                          icon="ZoomInIcon"
                          svgClasses="h-6 w-6"
                          style="color: white; cursor: pointer"
                          @click="zoomClicked('plus')"
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    v-else
                    class="hykecam-camera-area"
                    ref="hykeCamCameraArea"
                  >
                    <!-- ハイクカム用エリア -->
                    <div
                      class="hykecam-center"
                      :style="{ height: hykeCamHeightPx }"
                    >
                      <video
                        ref="hykeCamVideo"
                        class="media-video-player card-video vjs-big-play-centered hykecam-video"
                        autoplay
                        @click="hykeCamScreen"
                      />
                    </div>
                    <div
                      class="loader"
                      v-show="hykeCamLoaderShow"
                      :style="{
                        width: hykeCamLoaderWidthPx,
                        height: hykeCamLoaderHeightPx,
                        top: hykeCamLoaderTopPx,
                        left: hykeCamLoaderLeftPx,
                      }"
                    ></div>
                  </div>
                </div>

                <!-- onvifのみ: 垂直移動ゲージ -->
                <div
                  v-if="onvifActive"
                  class="flex h-full justify-between forward-layer"
                  :style="{
                    'min-width': `${onvifAreaSize.x}`,
                    height: `calc(100vh - ${videoPlayerAreaHeightOffset} - ${onvifAreaSize.y})`,
                  }"
                  style="
                    background-color: #4f4f4f;
                    flex-direction: column;
                    position: relative;
                  "
                >
                  <!-- onvif時の LIVEアイコン -->
                  <div style="position: absolute; top: 20px; left: -110px">
                    <div class="flex items-center px-4 py-1 live-icon-block">
                      <div class="material-icons">radio_button_checked</div>
                      <div class="ml-2">LIVE</div>
                    </div>
                  </div>

                  <div
                    class="flex w-full justify-center items-center mt-10"
                    style="flex-direction: column"
                  >
                    <div
                      class="ptz-manage-block relative"
                      :camera-id="camera.id"
                    >
                      <div class="ptz-ctl-box">
                        <!-- TODO: リファクタリング -->

                        <!-- onvifコントロールボタン -->
                        <div
                          class="flex w-full justify-center"
                          style="position: absolute; top: 0; left: 0"
                        >
                          <div
                            class="flex w-full justify-start relative"
                            style="width: 180px; height: 180px"
                          >
                            <!-- onvifコントロールボタン:外枠 180px -->
                            <div
                              class="flex"
                              style="
                                flex-wrap: wrap;
                                width: 180px;
                                height: 180px;
                                transform: rotate(45deg);
                                border-radius: 50%;
                              "
                            >
                              <div
                                id="ptz-double-up"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 90px;
                                  height: 90px;
                                  border: 1px solid #f2f2f2;
                                  border-radius: 100% 0 0 0;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-double-up') &&
                                    onvifPtzByScroll(0, 1, 0, cameraLargeMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: -8px;
                                    left: -8px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-double-up'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_double_arrow_up</span
                                  >
                                </div>
                              </div>
                              <div
                                id="ptz-double-right"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 90px;
                                  height: 90px;
                                  border: 1px solid #f2f2f2;
                                  border-radius: 0 100% 0 0;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-double-right') &&
                                    onvifPtzByScroll(1, 0, 0, cameraLargeMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: -8px;
                                    left: 8px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-double-right'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_double_arrow_right</span
                                  >
                                </div>
                              </div>
                              <div
                                id="ptz-double-left"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 90px;
                                  height: 90px;
                                  border: 1px solid #f2f2f2;
                                  border-radius: 0 0 0 100%;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-double-left') &&
                                    onvifPtzByScroll(-1, 0, 0, cameraLargeMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: 8px;
                                    left: -8px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-double-left'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_double_arrow_left</span
                                  >
                                </div>
                              </div>
                              <div
                                id="ptz-double-down"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 90px;
                                  height: 90px;
                                  border: 1px solid #f2f2f2;
                                  border-radius: 0 0 100% 0;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-double-down') &&
                                    onvifPtzByScroll(0, -1, 0, cameraLargeMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: 10px;
                                    left: 10px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-double-down'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_double_arrow_down</span
                                  >
                                </div>
                              </div>
                            </div>

                            <!-- onvifコントロールボタン: ローディングサークル 124px -->
                            <div
                              :class="[ptzClicked ? 'onvif-waiting' : null]"
                              style="
                                width: 124px;
                                height: 124px;
                                border-radius: 50%;
                                position: absolute;
                                top: 28px;
                                left: 28px;
                              "
                              :style="{
                                'background-color': ptzClicked
                                  ? '#2D9CDB'
                                  : '#F2F2F2',
                              }"
                            ></div>

                            <!-- onvifコントロールボタン: ローディング内枠 120px -->
                            <div
                              class="flex"
                              style="
                                flex-wrap: wrap;
                                width: 120px;
                                height: 120px;
                                position: absolute;
                                top: 30px;
                                left: 30px;
                                transform: rotate(45deg);
                                border-radius: 50%;
                                background-color: #2d9cdb;
                              "
                            >
                              <div
                                id="ptz-up"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 60px;
                                  height: 60px;
                                  border-right: 1px solid #2d9cdb;
                                  border-bottom: 1px solid #2d9cdb;
                                  border-radius: 100% 0 0 0;
                                  z-index: 8;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-up') &&
                                    onvifPtzByScroll(0, 1, 0, cameraSmallMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: -2px;
                                    left: -2px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-up'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_arrow_up</span
                                  >
                                </div>
                              </div>
                              <div
                                id="ptz-right"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 60px;
                                  height: 60px;
                                  border-bottom: 1px solid #2d9cdb;
                                  border-left: 1px solid #2d9cdb;
                                  border-radius: 0 100% 0 0;
                                  z-index: 8;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-right') &&
                                    onvifPtzByScroll(1, 0, 0, cameraSmallMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: -2px;
                                    left: 2px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-right'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_arrow_right</span
                                  >
                                </div>
                              </div>
                              <div
                                id="ptz-left"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 60px;
                                  height: 60px;
                                  border-top: 1px solid #2d9cdb;
                                  border-right: 1px solid #2d9cdb;
                                  border-radius: 0 0 0 100%;
                                  z-index: 8;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-left') &&
                                    onvifPtzByScroll(-1, 0, 0, cameraSmallMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: 2px;
                                    left: -2px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-left'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_arrow_left</span
                                  >
                                </div>
                              </div>
                              <div
                                id="ptz-down"
                                :class="[
                                  { 'onvif-control-button': !ptzClicked },
                                ]"
                                class="flex justify-center items-center m-0"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                }"
                                style="
                                  width: 60px;
                                  height: 60px;
                                  border-left: 1px solid #2d9cdb;
                                  border-top: 1px solid #2d9cdb;
                                  border-radius: 0 0 100% 0;
                                  z-index: 8;
                                  background-color: #2d9cdb;
                                "
                                @click="
                                  ptzClickEffect('ptz-down') &&
                                    onvifPtzByScroll(0, -1, 0, cameraSmallMove)
                                "
                              >
                                <div
                                  style="
                                    position: relative;
                                    top: 2px;
                                    left: 2px;
                                    transform: rotate(-45deg);
                                  "
                                >
                                  <span
                                    class="material-icons"
                                    :style="{
                                      color: ptzClicked
                                        ? ptzClicked === 'ptz-down'
                                          ? '#E2E2E2'
                                          : '#BDBDBD'
                                        : '#F2F2F2',
                                      opacity: ptzClicked ? 0.6 : 1,
                                    }"
                                    >keyboard_arrow_down</span
                                  >
                                </div>
                              </div>
                            </div>

                            <!-- onvifコントロールボタン: ホームボタン 70px -->
                            <div
                              id="ptz-home"
                              :class="[{ 'onvif-control-button': !ptzClicked }]"
                              class="flex justify-center items-center"
                              style="
                                width: 70px;
                                height: 70px;
                                border-radius: 50%;
                                background-color: #2d9cdb;
                                z-index: 9;
                                position: absolute;
                                top: 55px;
                                left: 55px;
                              "
                              @click="
                                ptzClickEffect('ptz-home') &&
                                  onvifGotoHomePosition()
                              "
                            >
                              <span
                                class="material-icons"
                                style="font-size: xx-large"
                                :style="{
                                  cursor: ptzClicked ? null : 'pointer',
                                  color: ptzClicked
                                    ? ptzClicked === 'ptz-home'
                                      ? '#E2E2E2'
                                      : '#BDBDBD'
                                    : '#F2F2F2',
                                  opacity: ptzClicked ? 0.6 : 1,
                                }"
                                >my_location</span
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- ボリュームコントロール -->
                    <div
                      class="flex w-full justify-center items-center mt-6 volume-controls"
                    >
                      <div
                        class="flex justify-start items-start ml-4"
                        style="flex-direction: column"
                      >
                        <div
                          class="mb-2"
                          style="color: #f2f2f2; font-size: 60%"
                        >
                          音量
                        </div>
                        <div
                          class="flex justify-center items-center px-0 py-2 pr-1 rounded"
                          style="background-color: #333333"
                        >
                          <div
                            class="material-icons volume-button cursor-pointer"
                            style="user-select: none"
                            :style="{
                              color:
                                Number(volume) === 0
                                  ? AISEA_COLOR.BLUE
                                  : AISEA_COLOR.WHITE,
                            }"
                            @click="onChangeVolume(Math.max(volume - 0.1, 0))"
                          >
                            volume_off
                          </div>
                          <input
                            class="volume"
                            style="background-color: #828282"
                            id="volume"
                            v-model="volume"
                            data-mute="0.5"
                            type="range"
                            max="1"
                            min="0"
                            step="0.01"
                            @change="onChangeVolume(volume)"
                          />
                          <div
                            class="material-icons volume-button cursor-pointer"
                            :style="{
                              color:
                                Number(volume) === 1
                                  ? AISEA_COLOR.BLUE
                                  : AISEA_COLOR.WHITE,
                            }"
                            style="user-select: none"
                            @click="onChangeVolume(Math.min(volume + 0.1, 1))"
                          >
                            volume_up
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- onvif時の Zoomアイコン -->
                    <div
                      class="flex w-full justify-between items-end mb-4 mt-8"
                    >
                      <div
                        class="flex justify-start items-start ml-8"
                        style="flex-direction: column"
                      >
                        <div
                          class="mb-2"
                          style="color: #f2f2f2; font-size: 60%"
                        >
                          拡大縮小
                        </div>
                        <div
                          class="flex justify-start items-center p-0 rounded"
                          style="background-color: #333333"
                        >
                          <div
                            id="ptz-zoom-out"
                            :class="[{ 'onvif-zoom-button': !ptzClicked }]"
                            class="material-icons mr-0 my-0 p-2"
                            :style="{
                              cursor: ptzClicked ? null : 'pointer',
                              color: ptzClicked
                                ? ptzClicked === 'ptz-zoom-out'
                                  ? '#E2E2E2'
                                  : '#BDBDBD'
                                : '#E2E2E2',
                              opacity: ptzClicked ? 0.8 : 1,
                            }"
                            style="
                              font-size: 28px;
                              border-radius: 0.2rem 0 0 0.2rem;
                            "
                            @click="
                              ptzClickEffect('ptz-zoom-out') &&
                                onvifPtzByScroll(0, 0, -1, cameraZoomMove)
                            "
                          >
                            remove
                          </div>
                          <div
                            id="ptz-zoom-split"
                            style="
                              min-height: 2.8rem;
                              width: 1px;
                              border: 1px solid #828282;
                            "
                          ></div>
                          <div
                            id="ptz-zoom-in"
                            :class="[{ 'onvif-zoom-button': !ptzClicked }]"
                            class="material-icons ml-0 my-0 p-2"
                            :style="{
                              cursor: ptzClicked ? null : 'pointer',
                              color: ptzClicked
                                ? ptzClicked === 'ptz-zoom-in'
                                  ? '#E2E2E2'
                                  : '#BDBDBD'
                                : '#E2E2E2',
                              opacity: ptzClicked ? 0.8 : 1,
                            }"
                            style="
                              font-size: 28px;
                              border-radius: 0 0.2rem 0.2rem 0;
                            "
                            @click="
                              ptzClickEffect('ptz-zoom-in') &&
                                onvifPtzByScroll(0, 0, 1, cameraZoomMove)
                            "
                          >
                            add
                          </div>
                        </div>
                      </div>
                      <div
                        class="flex justify-start items-start mr-6"
                        style="flex-direction: column"
                      >
                        <div
                          class="mb-2"
                          style="color: #f2f2f2; font-size: 60%"
                        >
                          全画面
                        </div>
                        <div
                          class="material-icons cursor-pointer onvif-zoom-button rounded p-2"
                          style="
                            font-size: 28px;
                            color: #f2f2f2;
                            background-color: #333333;
                          "
                          @click="setFullscreen(true)"
                        >
                          fullscreen
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="w-full relative"></div>
                </div>
              </div>
            </div>
            <div
              v-else
              id="camera-play-back-area"
              class="flex w-full justify-center items-start"
              style="height: calc(100vh - 140px); background-color: #f2f2f2"
            >
              <!-- CAMERA_MOVIE_TYPE.CAMERAの履歴の場合 -->
              <camera-play-back />
            </div>
          </div>

          <!-- 下部エリア： 時系列サムネイル: HLS_CAMERAのhistoryのみ -->
          <div
            v-if="
              mode === 'history' &&
              camera &&
              [CAMERA_MOVIE_TYPE.HLS_CAMERA, CAMERA_MOVIE_TYPE.SAFIE].includes(
                camera.movieType,
              )
            "
          >
            <vs-row style="margin-top: 4px">
              <vs-col class="w-full forward-layer relative">
                <div class="carousel-wrap pb-2" @scroll="onCarouselScroll">
                  <div
                    v-for="(thumbnailUrl, index) in thumbnailUrlList"
                    :key="index"
                    class="carousel-item flex relative rounded m-0"
                    :style="{
                      width: carouselItemWidth,
                      cursor:
                        thumbnailUrl.path !== noImageUrl ? 'pointer' : null,
                      border:
                        index === selectedHour ? ' 2px solid #48A0F4' : null,
                      'box-shadow':
                        index === selectedHour ? '0px 2px 6px #333333' : null,
                    }"
                    @click="setTargetHour(thumbnailUrl.path, index)"
                  >
                    <div
                      class="flex justify-center items-center"
                      style="color: white"
                      :style="{
                        'background-color':
                          index === selectedHour ? '#48A0F4' : '#333333',
                      }"
                    >
                      {{ ("00" + index).slice(-2) }}:00
                    </div>
                    <div
                      style="
                        width: 100%;
                        height: 120px;
                        box-sizing: border-box;
                        position: relative;
                        box-shadow: inset 2px 0px 0px #0000001f;
                        background-color: #bdbdbd;
                      "
                      :style="{
                        border:
                          thumbJstHHMM(thumbnailUrl.path) === targetHour
                            ? '4px solid #48A0F4'
                            : null,
                      }"
                    >
                      <img
                        v-if="thumbnailUrl.src"
                        :src="thumbnailUrl.src"
                        :style="{
                          width:
                            thumbnailUrl.path !== noImageUrl ? '100%' : '32px',
                        }"
                        style="
                          height: 100%;
                          position: absolute;
                          top: 0;
                          left: 0;
                          right: 0;
                          bottom: 0;
                          margin: auto;
                        "
                        crossorigin
                      />
                    </div>
                  </div>
                </div>
                <div
                  class="flex items-center jump-carousel-parent jump-carousel-parent--left"
                >
                  <div
                    class="flex items-center justify-center jump-carousel-button mt-8"
                    @click.stop="jumpCarouselPosition(-1)"
                  >
                    <feather-icon icon="ChevronLeftIcon" svgClasses="h-9 w-9" />
                  </div>
                </div>
                <div
                  class="flex items-center jump-carousel-parent jump-carousel-parent--right"
                >
                  <div
                    class="flex items-center justify-center jump-carousel-button mt-8"
                    @click.stop="jumpCarouselPosition(1)"
                  >
                    <feather-icon
                      icon="ChevronRightIcon"
                      svgClasses="h-9 w-9"
                    />
                  </div>
                </div>
              </vs-col>
            </vs-row>

            <vs-row style="height: 20px">
              <!-- スクロールブロック -->
              <vs-col
                class="flex w-full justify-center items-start forward-layer"
              >
                <div
                  @click="setCarouselPosition(4)"
                  class="carousel-summary-block m-2 cursor-pointer"
                  :style="{
                    'background-color':
                      carouselScrollRate <= 1 / 3
                        ? '#48A0F4'
                        : AISEA_COLOR.LIGHT_GRAY,
                  }"
                />
                <div
                  @click="setCarouselPosition(12)"
                  class="carousel-summary-block m-2 cursor-pointer"
                  :style="{
                    'background-color':
                      carouselScrollRate > 1 / 3 && carouselScrollRate < 2 / 3
                        ? '#48A0F4'
                        : AISEA_COLOR.LIGHT_GRAY,
                  }"
                />
                <div
                  @click="setCarouselPosition(20)"
                  class="carousel-summary-block m-2 cursor-pointer"
                  :style="{
                    'background-color':
                      carouselScrollRate >= 2 / 3
                        ? '#48A0F4'
                        : AISEA_COLOR.LIGHT_GRAY,
                  }"
                />
              </vs-col>
            </vs-row>
          </div>
        </div>
      </template>
    </vx-card>

    <div id="custom-breadcrumb-title" />

    <div v-for="(data, index) of thumbnailUrlList" :key="index">
      <div
        style="
          display: none;
          position: absolute;
          bottom: 0;
          right: 0;
          z-index: 99999;
          width: 140px;
          height: 120px;
        "
      >
        <video-player
          v-if="mode === 'history'"
          id="hidden-video-player"
          class="media-video-player card-video vjs-big-play-centered"
          :ref="`hiddenVideoPlayer${index}`"
          :options="playerOptions"
          :style="{ 'min-width': '100%', 'min-height': '100%' }"
        />
      </div>
    </div>
  </div>
</template>

<script>
// モジュール
import Vue from "vue";
import { videoPlayer } from "vue-videojs7";
import "vue-slider-component/theme/antd.css";
import videojs from "video.js";
import moment from "moment-timezone";
import _ from "lodash";
import DatepickerMixin from "@/mixins/DatepickerMixin";

// Mixin
import { CameraMixin, DeviceInfoMixin, ColorMixin } from "@/mixins";

// コンポーネント
import { OnvifCam as Cam } from "@/utils/onvif/OnvifCam";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { storageWebCam } from "@/firebase/firebaseConfig";
import { CAMERA_MODEL } from "@/config/const";
import { createNamespacedHelpers } from "vuex";
const { mapState, mapActions } = createNamespacedHelpers(
  "enterpriseCameraList",
);

// components
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { Japanese } from "flatpickr/dist/l10n/ja.js";

export default {
  components: {
    videoPlayer,
    flatPickr,
    CameraPlayerControl: () => import("./components/CameraPlayerControl"),
    CameraPlayBack: () => import("./components/CameraPlayBack"),
  },
  mixins: [CameraMixin, DatepickerMixin, DeviceInfoMixin, ColorMixin],
  props: {
    mode: { type: String, required: true },
  },
  data() {
    return {
      camera: null,
      cameraReady: false,
      angleHorizontal: 0,
      angleVertical: 0,
      sliderFormatter: (val) => `${val}°`,
      marksHorizontal: (val) =>
        val % 90 === 0
          ? {
              label: `${val}°`,
            }
          : false,
      marksVertical: (val) =>
        val % 10 === 0
          ? {
              label: `${val}°`,
            }
          : false,
      thumbnailUrlList: [],
      thumbnails: [],
      targetDateYmd: moment().local().startOf("date").toDate(),
      targetDate: moment().startOf("hour").toDate(),
      targetZoom: 5,
      onvifEnable: false,
      onvifActive: false,
      cameraZoomMin: 1,
      cameraZoomMax: 1,
      ptzClicked: null,

      canRealtime: true,

      selectedHour: -1,

      initialized: false,
      hasVideoError: false,
      retryText: "",
      cameraMsg: "",
      hlsSrc: null,
      cameraMode: {},
      dateList: {},
      errorDisplay: null,
      onvifBaseParameter: null,
      onvifMovingStopTimer: null,
      popupActive: false,
      pickerConfig: {
        playDate: null,
        playStartTime: null,
        enableDateList: null,
      },

      cameraModel: null,
      cameraSmallMove: null,
      cameraLargeMove: null,
      cameraZoomMove: null,

      seekCurrent: 0,
      intervalId: -1,
      videoPlayerWidthByHeight: "80%",
      zoomSize: "100%",
      trackIndexForSafie: 0,
      noImageUrl: require("@/assets/images/icons/cameras/video_off.svg"),
      carouselScrollRate: 0,
      cameraUpDownStep: 15,
      cameraRightLeftStep: 15,

      onvifCurrentAngleX: 0,
      onvifCurrentAngleY: 0,

      configDayPicker: {
        dateFormat: "Y/m/d",
        time_24hr: true,
        defaultDate: moment().startOf("hour").toDate(), // new Date(),
        enable: [],
        locale: Japanese,
      },
      lastMovieFileName: null,
      loadingFlg: false,
      fps: 1,
      carouselItemWidth: "160px",

      isProcessing: false,
      processingMessage: "",

      volume: 0,

      hykeCamTimer: null,
      hykeCamUrl: null,
      hykeCamHeight: "100%",
      hykeCamLoaderShow: false,
      hykeCamLoaderHeight: 100,
      hykeCamLoaderWidth: 100,
      hykeCamLoaderTop: 50,
      hykeCamLoaderLeft: 50,
    };
  },
  computed: {
    ...mapState({
      cameras: (state) => state.cameras,
    }),
    player() {
      if (typeof this.$refs.videoPlayer === "undefined") return null;
      return this.$refs.videoPlayer.player;
    },
    showTimeRangePicker() {
      return (
        this.camera.movieType !== this.CAMERA_MOVIE_TYPE.RADAR &&
        this.camera.movieType !== this.CAMERA_MOVIE_TYPE.HLS_RADAR
      );
    },
    playerOptions() {
      let playerOption = JSON.parse(
        JSON.stringify(this.getPlayerOption(this.camera.movieType)),
      );
      playerOption.controls = false;
      if (this.mode !== "history") {
        playerOption.autoplay = true;
      }
      return playerOption;
    },
    targetDateYmdSlash() {
      return this.targetDateYmd && this.targetDate
        ? moment(this.targetDate).format("YYYY/MM/DD")
        : "----/--/--";
    },
    targetHour() {
      return this.targetDateYmd && this.targetDate
        ? moment(this.targetDate).format("HH") + ":00"
        : "--:--";
    },
    playStartMomentValueOf() {
      return moment(this.targetDate).utc().unix().valueOf();
    },
    currentMomentValueOf() {
      return this.playStartMomentValueOf + this.seekCurrent;
    },

    zoomList() {
      const zoomList = [];
      for (let i = this.cameraZoomMin; i <= this.cameraZoomMax; i++) {
        zoomList.push(i);
      }
      return zoomList;
    },
    canPrevDate() {
      const prevDate = moment(this.targetDate)
        .local()
        .startOf("day")
        .add(-1, "days");
      return (
        this.configDayPicker.enable.length > 0 &&
        prevDate.format("YYYY-MM-DD") >= this.configDayPicker.enable[0]
      );
    },
    canNextDate() {
      const nextDate = moment(this.targetDate)
        .local()
        .startOf("day")
        .add(1, "days");
      return (
        nextDate.valueOf() < moment().valueOf() &&
        this.configDayPicker.enable.length > 0 &&
        nextDate.format("YYYY-MM-DD") <=
          this.configDayPicker.enable[this.configDayPicker.enable.length - 1]
      );
    },

    videoPlayerAreaHeightOffset() {
      if (
        this.mode === "history" &&
        this.camera &&
        [
          this.CAMERA_MOVIE_TYPE.HLS_CAMERA,
          this.CAMERA_MOVIE_TYPE.SAFIE,
        ].includes(this.camera.movieType)
      ) {
        return "320px"; // '310px';
      }
      return "140px";
    },
    onvifAreaSize() {
      return this.camera && this.onvifEnable && this.onvifActive
        ? // ? {x: '100px', y: '92px'}
          { x: "220px", y: " 0px" }
        : { x: "0px", y: "0px" };
    },
    thumbJstHHMM: () => (thumbnailUrl) => {
      const thumbnailUrlParts = thumbnailUrl.split("/");
      if (
        thumbnailUrlParts.length >= 3 &&
        !isNaN(thumbnailUrlParts[thumbnailUrlParts.length - 2])
      ) {
        const jstHH =
          (Number(thumbnailUrlParts[thumbnailUrlParts.length - 2]) + 9) % 24;
        return ("00" + jstHH).slice(-2) + ":00";
      }
      return "";
    },
    debugTargetDate() {
      return (
        moment(this.targetDate).format("HH:mm:ss") +
        ", " +
        moment(this.targetDate).utc().format("YYYYMMDD/HH") +
        ", " +
        moment(this.targetDate).toISOString() +
        ", " +
        this.targetDate
      );
    },

    isHykeCam() {
      return !this.camera
        ? false
        : this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM;
    },
    hykeCamHeightPx() {
      return this.hykeCamHeight + "px";
    },
    hykeCamLoaderHeightPx() {
      return this.hykeCamLoaderHeight + "px";
    },
    hykeCamLoaderWidthPx() {
      return this.hykeCamLoaderWidth + "px";
    },
    hykeCamLoaderTopPx() {
      return this.hykeCamLoaderTop + "px";
    },
    hykeCamLoaderLeftPx() {
      return this.hykeCamLoaderLeft + "px";
    },

    volumeIcon() {
      if (this.volume <= 0.01) return "VolumeXIcon";
      if (this.volume >= 0.01 && this.volume <= 0.5) return "Volume1Icon";
      return "Volume2Icon";
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth;
    },
  },
  watch: {
    targetDate: {
      handler: _.debounce(function () {
        this.trackIndexForSafie = 0;
        this.connect();
      }, 800),
    },
    targetZoom() {
      if (this.mode === "history") {
        // 過去データのZoom補正は、単純な画像引き伸ばし
        const zoomRateUnit = 1;
        this.zoomSize = `${this.targetZoom * zoomRateUnit * 100}%`;
      }
    },
    targetDateYmd() {
      if (this.mode === "history") {
        this.loadingFlg = true;
        this.$vs.loading({ container: "#camera-center-area" });
        // 時間変更ではなく、日付レベルで変化があった時。
        const initHourOnChange = 12; // 日付切り替え時の初期時間
        this.selectedHour = 12;
        this.targetDate = moment(this.targetDateYmd)
          .startOf("day")
          .add(initHourOnChange, "hour")
          .toDate();
        this.refreshThumbnailUrlList(this);
        setTimeout(() => {
          this.setCarouselPosition(initHourOnChange);
        }, 2000);
      }
    },
    onvifActive() {
      if (this.camera && this.onvifEnable) {
        this.connect();
        this.$nextTick(() => {
          this.recalcPlayerSize();
          if (!this.$refs.playBackControls.isPlay) {
            this.player.play();
            this.$refs.playBackControls.isPlay = true;
          }
        });
      }
    },
    onChangeWindowSize() {
      this.recalcPlayerSize();
      this.setCarouselItemWidth();
    },
    verticalNavMenuWidth() {
      setTimeout(() => {
        this.recalcPlayerSize();
        this.setCarouselItemWidth();
      }, 1200);
    },
  },

  created() {},
  async mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    await this.fetchCameras();
    this.camera = await this.cameras.find(
      (x) => x.id === this.$route.params.cameraId,
    );
    if (
      this.mode === "history" &&
      this.camera.movieType !== this.CAMERA_MOVIE_TYPE.CAMERA
    ) {
      await this.extractEnableDates(this.camera);
    }
    this.cameraReady = true;

    // 履歴指定の場合、date-pickerをアクティブに
    if (
      this.mode === "history" &&
      this.camera.movieType !== this.CAMERA_MOVIE_TYPE.CAMERA
    ) {
      this.$nextTick(() => {
        this.$el.querySelector("input.flatpickr-input").click();
        this.$el.querySelector("input.flatpickr-input").focus();
      });
    }

    // Playerの currentTimeを定期的に監視
    this.intervalId = setInterval(this.syncSeekPosition, 5000);

    this.$nextTick(async () => {
      this.recalcPlayerSize();
      this.setCarouselItemWidth();

      // パンくずリストのカスタマイズ
      this.customizeBreadcrumb();
    });

    // ハイクカム用の初期化
    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM) {
      window.addEventListener("resize", this.hykeCamResize, false);
      this.$refs.hykeCamVideo.addEventListener(
        "loadstart",
        this.hykeCamLoadStart,
        false,
      );
      this.$refs.hykeCamVideo.addEventListener(
        "loadeddata",
        this.hykeCamLoadedData,
        false,
      );
      this.hykeCamTimeout();
      this.hykeCamTimer = setInterval(this.hykeCamTimeout, 5000);
    }
  },
  beforeDestroy() {
    clearInterval(this.intervalId);

    // ハイクカム用のクリア処理
    if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HYKECAM) {
      clearInterval(this.hykeCamTimer);
    }
  },
  methods: {
    ...mapActions(["hykeCamGetMedia", "fetchCameras"]),

    // camera-connectメソッド群
    async connectToCamera() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").connect(
          this.camera.id,
        );
        return res.data.url;
      } catch (e) {
        return null;
      }
    },
    async connectToHlsCamera() {
      try {
        let res = await AiseaApiFactory.get("enterpriseCamera").connectHLS(
          this.camera.id,
        );
        return res.data.url;
      } catch (e) {
        return null;
      }
    },
    async connect() {
      if (
        this.mode === "history" &&
        this.camera.movieType === this.CAMERA_MOVIE_TYPE.CAMERA
      ) {
        this.$vs.loading.close("#camera-center-area > .con-vs-loading");
        this.loadingFlg = false;
        return;
      }
      if (!this.loadingFlg) {
        this.loadingFlg = true;
        this.$vs.loading({ container: "#camera-center-area" });
        setTimeout(() => {
          this.$vs.loading.close("#camera-center-area > .con-vs-loading");
          this.loadingFlg = false;
        }, 60 * 1000);
      }
      try {
        this.hasVideoError = false;
        this.cameraMsg = "";
        let url;

        if (
          (this.camera.movieType === this.CAMERA_MOVIE_TYPE.HLS_CAMERA ||
            this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE ||
            this.camera.movieType === this.CAMERA_MOVIE_TYPE.HLS_RADAR) &&
          !(
            this.initialized &&
            this.camera &&
            this.cameraReady &&
            this.onvifEnable &&
            this.onvifActive
          )
        ) {
          url = await this.connectToHlsCamera();
          this.hlsSrc = url;
          if (url) {
            if (this.mode !== "history" && this.camera.onvifUserName) {
              const urlForOnvif = await this.connectToCamera();
              if (urlForOnvif) {
                // onvifカメラではないと事前に検知できた場合は、そもそも初期化しない。
                this.onvifInitControl(urlForOnvif);
              }
            }

            this.canRealtime = await this.hasValidMovie(
              url,
              this.camera.movieType,
            );

            const isPastHour =
              moment(this.targetDate).startOf("hour").valueOf() <
                moment().startOf("hour").valueOf() ||
              (this.mode === "history" &&
                moment(this.targetDate).startOf("hour").valueOf() ===
                  moment().startOf("hour").valueOf());
            let trackUrl = null;
            if (isPastHour) {
              const ymdhUtc = moment(this.targetDate)
                .utc()
                .format("YYYYMMDD/HH");
              url = url.replace(/HLS.m3u8/i, `${ymdhUtc}/OnDemand.m3u8`);

              if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE) {
                // safieの履歴表示は、さらに10分単位に分割
                trackUrl = await this.convertOnDemandUrlForSafie(url, ymdhUtc);
              }
            }

            if (this.player && this.mode !== "history") {
              // TODO: 検証
              this.player.loop(true);
            }

            this.playVideoHls(url, trackUrl);

            if (isPastHour && this.player && this.mode === "history") {
              let seekCurrent = 0;
              if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE) {
                seekCurrent = this.seekCurrent;
              }
              this.seekCurrent = seekCurrent;
              this.player.currentTime(0);

              this.player.play();
              if (this.$refs.playBackControls) {
                this.$refs.playBackControls.isPlay = true;
                this.$refs.playBackControls.kickCreateThumbnails(url);
              }
              setTimeout(() => {
                this.player.playbackRate(this.fps);
              }, 1800);
            } else if (this.player && this.mode !== "history") {
              if (this.$refs.playBackControls) {
                this.$refs.playBackControls.isPlay = true;
              }
            }
          } else {
            this.errorHandler();
          }
        } else {
          url = await this.connectToCamera();
          this.hlsSrc = url + "/webcam/hls/stream.m3u8";
          if (url) {
            this.playVideo(url);
            if (this.$refs.playBackControls) {
              this.$refs.playBackControls.isPlay = true;
            }
            if (this.camera.onvifUserName) {
              // onvifカメラではないと事前に検知した場合は、そもそも初期化しない。
              this.onvifInitControl(url);
            }
          } else {
            this.errorHandler();
          }
        }
      } catch (e) {
        Vue.$log.debug("problem at connect()", e);
      }

      setTimeout(() => {
        this.$vs.loading.close("#camera-center-area > .con-vs-loading");
        this.loadingFlg = false;
      }, 1000);

      if (this.mode === "history") {
        this.targetZoom = 1;
        this.cameraZoomMin = 1;
        this.cameraZoomMax = 5;
      }
    },
    playVideo(bassUrl) {
      if (this.player) {
        this.cameraMsg = "";
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: bassUrl + "/webcam/hls/stream.m3u8",
        };
        this.hlsSrc = bassUrl + "/webcam/hls/stream.m3u8";
        this.player.src(video);
        this.player.poster(bassUrl + "/webcam/thumbnail/thumbnail.png");
      }
    },
    playVideoHls(baseUrl, trackUrl) {
      if (this.player) {
        this.cameraMsg = "";
        this.player.reset();
        const video = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: trackUrl ? trackUrl : baseUrl,
        };
        this.hlsSrc = baseUrl;
        this.player.src(video);
        let path = baseUrl.split("/");
        path.pop();
        const thumbnailUrl =
          this.convertGcsPath(path.join("/")) + "/thumbnail.png";
        this.player.poster(thumbnailUrl);
      }
    },
    errorHandler() {
      if (this.player) {
        this.player.reset();
        let ErrorDisplay = videojs.getComponent("ErrorDisplay");
        this.errorDisplay = new ErrorDisplay(this.player, {
          temporary: false,
        });
        this.player.addChild(this.errorDisplay);
        this.cameraMsg = "";
        this.player.error(this.getCameraErrorMessage(this.camera.movieType));
        this.retryText = "もう一度接続";
        this.hasVideoError = true;
      }
    },
    async convertOnDemandUrlForSafie(url, ymdhUtc) {
      // safieの履歴表示は、m3u8ファイルを10分単位に再構成
      const m3u8OrigText = await fetch(url, { mode: "cors" }).then((res) =>
        res.text(),
      );
      const m3u8OrigList = m3u8OrigText.split("\n");
      const m3u8List = [];
      const filterRegExp = new RegExp(
        ymdhUtc.replace(/\//, "-") + this.trackIndexForSafie,
        "i",
      );
      let prevLine = "";
      for (const line of m3u8OrigList) {
        if (line.match(/^#/) && !line.match(/^#EXTINF:/)) {
          m3u8List.push(line);
        } else if (line.match(/\.ts$/)) {
          if (line.match(filterRegExp)) {
            m3u8List.push(prevLine);
            m3u8List.push(line);
          }
        }
        prevLine = line;
      }

      const m3u8Text = m3u8List.join("\n");
      url = URL.createObjectURL(new Blob([m3u8Text], { type: "text/plain" }));
      return url;
    },

    async hasValidMovie(url, movieType) {
      const graceMinutes = 30;

      // m3u8の最後の動画ファイル名を取得する。
      const playListText = await fetch(url, { mode: "cors" }).then((res) =>
        res.text(),
      );
      const playListLines = playListText
        .split("\n")
        .filter((x) => x.match(/\.ts$/));

      if (
        movieType === this.CAMERA_MOVIE_TYPE.SAFIE &&
        playListLines.length > 0
      ) {
        // Safieは ファイル名から時間を割り出せないので、firestore.metadataで判定
        if (playListLines.length === 0) {
          return false;
        } else {
          const webcamChildPath = decodeURIComponent(
            playListLines[playListLines.length - 1].replace(
              new RegExp(process.env.VUE_APP_CLOUD_SOTRAGE_WEBCAM_URL),
              "",
            ),
          );
          const ref = storageWebCam.child(webcamChildPath);
          const metadata = await ref.getMetadata();
          const diffMinutes =
            (moment().valueOf() - moment(metadata["updated"]).valueOf()) /
            (60 * 1000);
          return diffMinutes < graceMinutes;
        }
      }

      const lastMovieFileName =
        playListLines.length > 0
          ? playListLines[playListLines.length - 1]
          : null;
      if (lastMovieFileName) {
        // https://storage.googleapis.com/aisea-webcam/fa2dd7ab01437c01d86726af6e7e63e2f145347fb4e4a82a6589d6260abec3f1/20220817/17/20220817-173000%2C10.ts
        // 20220817-173000%2C10.ts
        const ymdhmsZ =
          lastMovieFileName
            .replace(/^.*\//, "")
            .replace(/-/g, "_")
            .replace(/^([0-9_]+).*$/, "$1") + "Z";
        const lastMomentValue = moment(ymdhmsZ, "YYYYMMDD_HHmmssZ").valueOf();
        if (lastMomentValue + graceMinutes * 60 * 1000 > moment().valueOf()) {
          // m3u8の最後の動画ファイル名 から読み取れる時間が直近であれば、Live有効な動画と判定
          return true;
        } else {
          Vue.$log.debug(
            "movie latest timestamp",
            this.camera.id,
            this.camera.movieType,
            ymdhmsZ,
            ((moment().valueOf() - lastMomentValue) / (60 * 1000)).toFixed(1) +
              "min.",
          );
        }
      }
      return false;
    },

    // video-player用メソッド群
    async onPlayerReadied() {
      if (!this.initialized && this.cameraReady === true) {
        await this.connect();
        this.initialized = true;
        if (
          this.mode === "history" &&
          [
            this.CAMERA_MOVIE_TYPE.HLS_CAMERA,
            this.CAMERA_MOVIE_TYPE.SAFIE,
          ].includes(this.camera.movieType) &&
          this.trackIndexForSafie === 0
        ) {
          this.refreshThumbnailUrlList(this);
        }
      }
    },
    onPlayerPlay() {
      this.hidePlayButton();
      this.player.error(null);
    },
    onPlayerPause() {
      this.showPlayButton();
    },
    onPlayerError(player) {
      const errorCode = player && player.error() && player.error().code;
      if (errorCode !== 3) {
        this.cameraMsg = "";
        this.retryText = "もう一度接続";
        this.hidePlayButton();
        this.hasVideoError = true;
        if (this.$refs.playBackControls) {
          this.$refs.playBackControls.setPause();
        }
      } else {
        if (this.mode !== "history") {
          // 3: MEDIA_ERR_DECODE でエラーになった場合は 再接続をコール
          player.error(null);
          this.connect().then(() => {
            if (this.player) {
              this.player.play();
            }
          });
        } else if (this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE) {
          // Safieの履歴表示でエラーになった時、トラックの境界線に当たった可能性があるので、seekCurrent時間を少し進めて再コール
          player.error(null);
          const seekCurrent = this.seekCurrent + 30;
          const playerCurrentTime = seekCurrent % 600;
          const trackIndexForSafie = Math.floor(seekCurrent / 600);
          this.trackIndexForSafie = trackIndexForSafie;
          this.seekCurrent = seekCurrent;
          this.connect().then(() => {
            if (this.player) {
              this.player.play();
              for (let i = 0; i < 5000 / 100 / 2; i++) {
                setTimeout(() => {
                  this.player.currentTime(playerCurrentTime);
                  this.seekCurrent = seekCurrent;
                }, i * 100);
              }
            }
          });
        }
      }
    },
    onPlayerEnded() {
      if (
        this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE &&
        this.mode === "history"
      ) {
        if (this.trackIndexForSafie < 6) {
          // SafieでPlayerEndedを検知した時、次のトラックをコール
          this.trackIndexForSafie += 1;
          this.connect();
        }
        return;
      }

      if (this.existForward) {
        this.onSkipForward();
      }

      this.player.play();
    },
    onSkipForward() {
      this.playNextOnDemand(3600);
      this.existBack = true;
    },
    playNextOnDemand(offset) {
      let utcStartTime = moment.unix(this.start + offset);
      this.start = moment(utcStartTime).unix();
      let utcEndTime = moment.unix(this.end + offset);
      this.end = moment(utcEndTime).unix();
      let path = this.baseUrl.split("/");
      path.pop();
      path.pop();
      this.baseUrl =
        path.join("/") + moment(utcStartTime).utc().format("/YYYYMMDD/HH");
      this.srcUrl = this.baseUrl + "/OnDemand.m3u8";
      this.posterUrl = this.baseUrl + "/thumbnail.png";
      let currentTime = this.currentTime;
      this.playOnDemand();
      this.checkNextOnDemand(offset);
      if (offset > 0) {
        this.backTime = currentTime;
      } else {
        this.forwardTime = currentTime;
      }
    },
    playOnDemand() {
      if (this.player && this.srcUrl) {
        this.player.src({
          withCredentials: false,
          type: "application/x-mpegurl",
          src: this.srcUrl,
        });
        this.player.poster(this.posterUrl);
        // m3u8はHLSの機能なので、1Hくらい遡っておく
        this.player.currentTime(-3650);
        this.showPlayButton();
        this.popupActive = true;
      }
    },
    async checkNextOnDemand(offset) {
      let utcStartTime = moment.unix(this.start + offset);
      let utcEndTime = moment.unix(this.end + offset);
      let path = this.baseUrl.split("/");
      path.pop();
      path.pop();
      let baseUrl =
        path.join("/") + moment(utcStartTime).utc().format("/YYYYMMDD/HH");
      let res = await this.existOnDemand(baseUrl + "/OnDemand.m3u8");
      let startLocal = utcStartTime
        .tz("Asia/Tokyo")
        .format("YYYY年MM月DD日 HH:mm");
      let endLocal = utcEndTime.tz("Asia/Tokyo").format("HH:mm");
      if (!res) {
        if (offset > 0) {
          this.existForward = false;
          this.forwardTime = `${startLocal} - ${endLocal}`;
        } else {
          this.existBack = false;
          this.backTime = `${startLocal} - ${endLocal}`;
        }
      } else {
        if (offset > 0) {
          this.existForward = true;
          this.forwardTime = `${startLocal} - ${endLocal}`;
        } else {
          this.existBack = true;
          this.backTime = `${startLocal} - ${endLocal}`;
        }
      }
    },
    async existOnDemand(src) {
      try {
        await fetch(src, { mode: "cors" });
      } catch (err) {
        return false;
      }
      return true;
    },
    showPlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      playButton[0].style.display = "block";
    },
    hidePlayButton() {
      let playButton = this.player
        .contentEl()
        .getElementsByClassName("vjs-big-play-button");
      playButton[0].style.display = "none";
    },

    // コントロールバーエリアからのイベントドリブンメソッド群
    onPlay() {
      if (this.$refs.playBackControls.isPlay) {
        this.player.play();
      } else {
        this.player.pause();
      }
    },
    onChangeVolume(volume) {
      this.volume = volume;
      this.player.volume(volume);
      if (volume === 0) {
        this.player.muted(true);
      } else if (volume > 0 && volume <= 1) {
        this.player.muted(false);
      }
    },
    setFPS(fps) {
      this.player.pause();
      try {
        this.player.playbackRate(fps);
        this.fps = fps;
      } catch (e) {
        this.fps = 1;
        Vue.$log.warn("playbackRate error", e);
      }
      this.$nextTick(() => {
        this.player.play();
      });
    },
    onChangeSeek(currentTime) {
      const seekTime = currentTime;
      this.onChangeSeekCurrent(seekTime);
    },
    onChangeSeekCurrent(val) {
      if (this.player) {
        this.setCurrentSecond(this, val);
      }
    },
    setCurrentSecond: _.debounce((self, val) => {
      if (self.camera.movieType !== self.CAMERA_MOVIE_TYPE.SAFIE) {
        self.seekCurrent = Math.ceil(val / 5) * 5;
        self.player.currentTime(self.seekCurrent);
      } else {
        const seekCurrent = Math.ceil(val / 5) * 5;
        const playerCurrentTime = seekCurrent % 600;
        const trackIndexForSafie = Math.floor(seekCurrent / 600);
        if (self.trackIndexForSafie !== trackIndexForSafie) {
          // Safieで seek時間が別のブロックを指した時、connect()をコールする。
          self.trackIndexForSafie = trackIndexForSafie;
          self.seekCurrent = seekCurrent;
          self.connect().then(() => {
            self.player.currentTime(playerCurrentTime);
            self.seekCurrent = seekCurrent;
            for (let i = 0; i < 5000 / 100 / 2; i++) {
              setTimeout(() => {
                self.player.currentTime(playerCurrentTime);
                self.seekCurrent = seekCurrent;
              }, i * 100);
            }
          });
        } else {
          self.player.currentTime(playerCurrentTime);
          self.seekCurrent = seekCurrent;
        }
      }
      setTimeout(() => {
        self.syncSeekPosition();
      }, 1000);
    }, 400),
    setFullscreen(isFullscreen) {
      this.isFullscreen = isFullscreen;
      if (isFullscreen) {
        this.player.requestFullscreen();
      } else {
        this.$refs.playBackControls.setStyleToSmall();
      }
    },
    onFullscreenChange() {
      // Escボタンによるフルスクリーン解除をフック
      if (!document.fullscreenElement) {
        this.$refs.playBackControls.setStyleToSmall();
      }
    },

    // カレンダーの有効日付（映像データが存在する）のリスト抽出
    async extractEnableDates(camera) {
      let enableDateList = [];
      storageWebCam
        .child(camera.hash)
        .listAll()
        .then((response) => {
          if (response.prefixes.length == 0) {
            this.$vs.notify({
              color: "danger",
              title: "指定されたカメラの録画データがみつかりませんでした",
            });
          } else {
            _.forEach(
              response.prefixes.filter((prefix) =>
                prefix.name.match(/^[0-9]{8}/),
              ),
              function (item) {
                enableDateList.push(
                  moment(item.name).utc().tz("Asia/Tokyo").format("YYYY-MM-DD"),
                );
              },
            );

            this.configDayPicker.enable = enableDateList;
            this.targetDateYmd = moment(
              enableDateList[enableDateList.length - 1],
              ["YYYY-MM-DD"],
            ).toDate();
          }
        })
        .catch(() => {
          this.$vs.notify({
            color: "danger",
            title: "カメラの録画データ取得に失敗しました",
          });
        });
    },
    // カレンダーを１日移動
    incrementTargetDate(val) {
      this.loadingFlg = true;
      this.$vs.loading({ container: "#camera-center-area" });

      let candidateMoment = null;
      for (let i = 1; i < 365 * 3; i++) {
        // 無効日を避けて次の日付を決める。
        candidateMoment = moment(this.targetDate)
          .add(val * i, "day")
          .startOf("day");
        if (candidateMoment.valueOf() > moment().valueOf()) {
          // 未来日には行けない
          this.loadingFlg = false;
          this.$nextTick(() => {
            this.$vs.loading.close("#camera-center-area > .con-vs-loading");
          });
          return;
        } else if (
          !this.configDayPicker.enable
            .map((x) => moment(x, ["YYYY-MM-DD"]).toDate().getTime())
            .includes(candidateMoment.toDate().getTime())
        ) {
          // 無効日の場合は、翌日or前日にずらして再判定
          continue;
        } else {
          // 上記以外なら、有効日として日付変更を開始
          this.targetDateYmd = candidateMoment.toDate();
          break;
        }
      }
      this.loadingFlg = false;
      this.$nextTick(() => {
        this.$vs.loading.close("#camera-center-area > .con-vs-loading");
      });
      return;
    },

    // カメラエリアの表示サイズ最適化
    recalcPlayerSize() {
      const videoPlayerAreaElement =
        document.getElementById("video-player-area");
      if (videoPlayerAreaElement && this.player) {
        const rect = this.player.el_.getBoundingClientRect();
        const adjustedWidth =
          (videoPlayerAreaElement.getBoundingClientRect().height * rect.width) /
          rect.height;
        if (
          this.camera.movieType === this.CAMERA_MOVIE_TYPE.SAFIE &&
          this.mode === "history"
        ) {
          this.videoPlayerWidthByHeight = adjustedWidth - 180 + "px";
        } else {
          this.videoPlayerWidthByHeight = adjustedWidth + "px";
        }
      }
    },

    // カメラ表示エリアをクリックした時
    onVideoClick() {
      this.$refs.playBackControls.onPlay();
    },

    // Playerの currentTimeを定期的に監視
    syncSeekPosition() {
      if (this.player && this.initialized && !this.thumbnailProcessingFlg) {
        if (this.camera.movieType !== this.CAMERA_MOVIE_TYPE.SAFIE) {
          this.seekCurrent = this.player.currentTime();
        } else {
          // Safieの場合、ローディング中は seekPositionのsyncをやめる。
          if (this.loadingFlg === false) {
            this.seekCurrent =
              this.player.currentTime() + this.trackIndexForSafie * 600;
          }
        }
      }
    },

    // 一覧へ戻るをクリックした時
    toListClicked() {
      this.$router.push({
        name: "enterprise-management-camera-list",
      });
    },

    // live <--> 履歴 切り替えボタンをクリックした時
    changeMode() {
      const nextPageName =
        this.mode === "history"
          ? "enterprise-management-camera-view-realtime"
          : "enterprise-management-camera-view-history";
      const routerPush = {
        name: nextPageName,
        params: { cameraId: this.camera.id },
      };
      this.$router.push(routerPush);
    },

    // 時系列サムネイルリストが横スクロールされた時
    onCarouselScroll() {
      const carouselWrapEl = this.$el
        ? this.$el.querySelector(".carousel-wrap")
        : null;
      this.carouselScrollRate = carouselWrapEl
        ? carouselWrapEl.scrollLeft /
          (carouselWrapEl.scrollWidth -
            carouselWrapEl.getBoundingClientRect().width)
        : null;
    },
    // 時系列サムネイルリストをブロック単位でジャンプさせるとき
    jumpCarouselPosition(val) {
      const el = this.$el.querySelector("div.carousel-wrap");
      const scrollUnitSize =
        el.getBoundingClientRect().width * 0.8 * (val > 0 ? +1 : -1);
      el.scrollTo(el.scrollLeft + scrollUnitSize, 0);
    },
    // 時系列サムネイルリストの横スクロールを時刻に合わせてセットするとき
    setCarouselPosition(hour) {
      const el = this.$el.querySelector("div.carousel-wrap");
      if (el) {
        const scrollPosition =
          (el.getBoundingClientRect().width * (hour - 4)) / 8;
        el.scrollTo(scrollPosition, 0);
      }
    },

    // 時系列サムネイルをクリック
    setTargetHour(thumbnailUrl, hour) {
      if (thumbnailUrl === this.noImageUrl) {
        return;
      }
      let thumbnailUrlParts = thumbnailUrl.split("/");
      if (thumbnailUrl === this.iconOnvif) {
        const yyyymmdd = moment(this.targetDate)
          .startOf("day")
          .utc()
          .format("YYYYMMDD");
        thumbnailUrlParts = [yyyymmdd, ("00" + hour).slice(-2), ""];
      }
      if (thumbnailUrlParts.length >= 3) {
        const ymdh = `${thumbnailUrlParts[thumbnailUrlParts.length - 3]}${
          thumbnailUrlParts[thumbnailUrlParts.length - 2]
        }`;
        if (ymdh.match(/[0-9]{10}/)) {
          this.loadingFlg = true;
          this.$vs.loading({ container: "#camera-center-area" });
          const timeString = ymdh.replace(
            /([0-9]{4})([0-9]{2})([0-9]{2})([0-9]{2})/,
            "$1-$2-$3T$4:00:00.000Z",
          );
          this.targetDate = moment(timeString).toDate();
          this.selectedHour = hour;
        }
      }
    },

    // 時系列サムネイルリストの生成
    refreshThumbnailUrlList: _.debounce(async (self) => {
      let thumbnailPatchUpList = [];
      if (self.hlsSrc) {
        self.thumbnailUrlList = [];
        const baseDateMoment = moment(self.targetDate).startOf("day");

        // Vue.$log.debug("### baseDateMoment", baseDateMoment.format('YYYYMMDD/HH'), baseDateMoment.utc().format('YYYYMMDD/HH'), self.hlsSrc );
        // https://storage.googleapis.com/aisea-webcam/06728e88b6c1eb24f3cc92b50e6a069fce556448c8e8b790e0cb889d0b2151cf/HLS.m3u8

        // https://api.aisea.dev/v1/admin/enterprise/cameras/30e2413e2e21bf527b697f2c0f09f3ed3e2d993b9967fe0445ffa80d9122bb39/hls.m3u8
        // https://storage.googleapis.com/aisea-webcam/30e2413e2e21bf527b697f2c0f09f3ed3e2d993b9967fe0445ffa80d9122bb39/20220527/05/thumbnail.png

        // GCS側のPATHを算出
        const hlsSrc = self.convertGcsPath(self.hlsSrc);

        let thumbnailUrlList = [];
        for (let i = 0; i < 24; i++) {
          const targetDateYmdhUtc = moment(baseDateMoment)
            .utc()
            .add(i, "h")
            .format("YYYYMMDD/HH");
          const thumbnailUrl = hlsSrc
            .replace(/HLS.m3u8$/i, `${targetDateYmdhUtc}/thumbnail.png`)
            .replace(
              /[0-9]{8}\/[0-9]{2}\/OnDemand.m3u8$/,
              `${targetDateYmdhUtc}/thumbnail.png`,
            );
          const isExists = await self.isExistsThumbnail(thumbnailUrl);

          if (isExists) {
            thumbnailUrlList.push({ src: thumbnailUrl, path: thumbnailUrl });
          } else {
            thumbnailUrlList.push({
              src: self.noImageUrl,
              path: self.noImageUrl,
            });

            // thumbnail.png が GCS上に存在しない場合、インデックス番号を覚えておき、tsファイルがあればそれを使って後から差し込む
            thumbnailPatchUpList.push({ hour: i, ymdh: targetDateYmdhUtc });
          }
        }
        self.generateThumbnailPatchUp(thumbnailPatchUpList);
        self.thumbnailUrlList = thumbnailUrlList;
      }
    }, 400),
    async isExistsThumbnail(thumbnailUrl) {
      try {
        const httpRes = await fetch(thumbnailUrl, { mode: "cors" });
        if (Math.floor(httpRes.status / 100) === 2) {
          return true;
        }
      } catch (e) {
        Vue.$log.debug("### get thumbnailUrl err", e);
      }
      return false;
    },
    async generateThumbnailPatchUp(thumbnailPatchUpList) {
      // 最初のtsファイルを使ってサムネイルの生成。

      thumbnailPatchUpList.forEach(async (patchUpInfo) => {
        const hour = patchUpInfo.hour;
        const targetDateYmdhUtc = patchUpInfo.ymdh;

        const targetHourOndemandUrl = this.hlsSrc
          .replace(/HLS.m3u8$/i, `${targetDateYmdhUtc}/OnDemand.m3u8`)
          .replace(
            /[0-9]{8}\/[0-9]{2}\/OnDemand.m3u8$/,
            `${targetDateYmdhUtc}/OnDemand.m3u8`,
          );
        const thumbnailUrl = targetHourOndemandUrl.replace(
          /OnDemand.m3u8/i,
          "thumbnail.png",
        );
        const videoSrc = {
          withCredentials: false,
          type: "application/x-mpegurl",
          src: targetHourOndemandUrl,
        };

        let hiddenVideoPlayerObj = null;
        for (let i = 0; i < 10; i++) {
          hiddenVideoPlayerObj =
            this.$refs[`hiddenVideoPlayer${hour}`] &&
            this.$refs[`hiddenVideoPlayer${hour}`][0]
              ? this.$refs[`hiddenVideoPlayer${hour}`][0]
              : null;
          if (hiddenVideoPlayerObj) {
            break;
          }
          await this.sleep(1);
        }

        let hiddenPlayer = hiddenVideoPlayerObj.player;
        hiddenPlayer.reset();
        hiddenPlayer.src(videoSrc);

        hiddenPlayer.on("ready", async () => {
          let captureCount = 0;
          for (let i = 0; i < 20; i++) {
            await this.sleep(1);
            // 動画がロードされてからサムネイルを順次キャプチャ。
            const video = hiddenVideoPlayerObj.$el.querySelector("video");
            if (video && video.videoWidth > 0 && video.videoHeight > 0) {
              const canvas = document.createElement("canvas");
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              const context = canvas.getContext("2d");
              context.drawImage(
                video,
                0,
                0,
                video.videoWidth,
                video.videoHeight,
              );
              let binUrl = canvas.toDataURL("image/jpeg");
              this.$set(this.thumbnailUrlList, hour, {
                src: binUrl,
                path: thumbnailUrl,
              });
              captureCount++;
              if (captureCount >= 4) {
                break;
              }
            }
          }
        });
      });
    },
    sleep(waitSec) {
      return new Promise(function (resolve) {
        setTimeout(function () {
          resolve();
        }, waitSec * 1000);
      });
    },

    // パンくずリストのカスタマイズ
    customizeBreadcrumb() {
      const customBreadcrumbTitleEl = document.getElementById(
        "custom-breadcrumb-title",
      );
      if (customBreadcrumbTitleEl) {
        const breadCrumbItemName = this.camera.name;
        this.$nextTick(() => {
          // div.vx-breadcrumb ul を直接変更すると、 他画面のパンくずにまで影響するので、コピーdivを生成し、該当箇所に被せる。
          if (!document.getElementById("custom-breadcrumb-item-name")) {
            const basePositionEl = document.querySelector("div.router-view");
            const ulElement = document.querySelector("div.vx-breadcrumb ul");
            customBreadcrumbTitleEl.style["left"] =
              ulElement.children[
                ulElement.children.length - 1
              ].getBoundingClientRect().left -
              basePositionEl.getBoundingClientRect().left +
              "px";
            customBreadcrumbTitleEl.style["top"] =
              ulElement.getBoundingClientRect().top + "px";
            customBreadcrumbTitleEl.style["height"] =
              ulElement.getBoundingClientRect().height + "px";
            const breadcrumbItemCopyEl =
              ulElement.children[ulElement.children.length - 1].cloneNode(true);
            customBreadcrumbTitleEl.appendChild(
              breadcrumbItemCopyEl.firstChild.cloneNode(true),
            );
            customBreadcrumbTitleEl.lastChild.id =
              "custom-breadcrumb-item-name";
          }
          customBreadcrumbTitleEl.lastChild.innerText = breadCrumbItemName;
        });
      }
    },

    // GCS側のPATHを算出
    convertGcsPath(apiUrl) {
      return apiUrl.replace(
        /^https:\/\/[^/]+\/v1\/admin\/enterprise\/cameras/,
        process.env.VUE_APP_CLOUD_SOTRAGE_WEBCAM_URL.replace(/\/$/, ""),
      );
    },

    // hykeCam の メソッド群
    async hykeCamTimeout() {
      let payload = {
        cameraId: this.camera.id,
      };
      let res = await this.hykeCamGetMedia(payload);
      if (this.hykeCamUrl != res.url) {
        this.hykeCamUrl = res.url;
        this.$refs.hykeCamVideo.src = this.hykeCamUrl;
      }
    },
    async hykeCamResize() {
      if (this.$refs.hykeCamVideo) {
        this.hykeCamHeight = this.$refs.hykeCamVideo.clientHeight;
      }
    },
    async hykeCamLoadStart() {
      this.hykeCamLoaderHeight = this.$refs.hykeCamCameraArea.clientHeight / 4;
      this.hykeCamLoaderWidth = this.hykeCamLoaderHeight;
      this.hykeCamLoaderTop =
        this.$refs.hykeCamCameraArea.clientHeight / 2 -
        this.hykeCamLoaderHeight / 2;
      this.hykeCamLoaderLeft =
        this.$refs.hykeCamCameraArea.clientWidth / 2 -
        this.hykeCamLoaderWidth / 2;
      this.hykeCamLoaderShow = true;
    },
    async hykeCamLoadedData() {
      this.hykeCamLoaderShow = false;
      this.hykeCamHeight = this.$refs.hykeCamVideo.clientHeight;
    },
    async hykeCamScreen() {
      const video = this.$refs.hykeCamVideo;
      if (video.requestFullScreen) {
        video.requestFullScreen();
      } else if (video.webkitRequestFullScreen) {
        video.webkitRequestFullScreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      } else if (video.mozRequestFullScreen) {
        video.mozRequestFullScreen();
      } else if (video.msRequestFullscreen) {
        video.msRequestFullscreen();
      }
    },

    /*** onvifメソッドの呼び出しサンプル ***START*** */

    /**
     * [onvif]初期パラメータのセットと接続確認.
     */
    onvifInitControl(url) {
      const onvifUrl = url.replace(/^https?:\/\//, "").replace(/\/$/, "");
      const onvifHostName =
        onvifUrl.split(":").length > 1 ? onvifUrl.split(":")[0] : onvifUrl;
      const onvifPort =
        onvifUrl.split(":").length > 1 ? onvifUrl.split(":")[1] : 443;

      this.onvifBaseParameter = {
        hostname: onvifHostName,
        username: this.camera.onvifUserName,
        password: this.camera.onvifPassword,
        port: onvifPort,
        timeout: 5000,
      };
      Vue.$log.debug(
        `###  onvifBaseParameter(${this.camera.id})`,
        this.onvifBaseParameter,
      );

      let self = this;
      const mine = this;

      // onvif接続可能か、さらにptz操作可能かを確認。
      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.debug("カメラにONVIF接続できませんでした。", err);
          return;
        }
        if (this.capabilities.PTZ === undefined) {
          Vue.$log.debug("カメラはPTZ操作をサポートしていません。");
          return;
        }

        let onvifApi = this;
        onvifApi.getDeviceInformation(function (err, res) {
          // function(err, res, xml){
          let model;
          if (
            res.model === CAMERA_MODEL.properties[CAMERA_MODEL.HV_PTZ500].model
          ) {
            model = CAMERA_MODEL.HV_PTZ500;
          } else if (
            res.model === CAMERA_MODEL.properties[CAMERA_MODEL.WV_S6530].model
          ) {
            model = CAMERA_MODEL.WV_S6530;
          } else if (
            res.model === CAMERA_MODEL.properties[CAMERA_MODEL.HV_PTZ501].model
          ) {
            model = CAMERA_MODEL.HV_PTZ501;
          } else {
            model = CAMERA_MODEL.OTHERS;
          }
          mine.cameraModel = res.model;
          mine.cameraSmallMove = CAMERA_MODEL.properties[model].smallMove;
          mine.cameraLargeMove = CAMERA_MODEL.properties[model].largeMove;
          mine.cameraZoomMove = CAMERA_MODEL.properties[model].zoomMove;
        });

        Vue.$log.debug(
          "### カメラにONVIF接続しました。 this.capabilities",
          this.capabilities,
        );
        self.onvifEnable = true;
      });
    },

    /**
     * [onvif]PTZ操作(パターンA:指定方向に指定角度でジャンプ).
     */
    onvifPtzByStep(xDirection, yDirection, zoomDirection, movingAngle) {
      const xStep = (xDirection * movingAngle) / 360; // -1〜+1 = -360〜+360度
      const yStep = (yDirection * movingAngle) / 90; // -1〜+1 = -90〜+90度
      const zoomStep = zoomDirection / 10; // -1〜+1 = ズーム最小〜最大
      const onvifProfileToken = this.camera.onvifProfileToken;

      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }
        let onvifApi = this;

        onvifApi.relativeMove(
          {
            profileToken: onvifProfileToken,
            x: xStep,
            y: yStep,
            zoom: zoomStep,
          },
          function (err, responseData, rawXml) {
            // for debug.
            Vue.$log.debug("relativeMove[xml] = ", rawXml);
          },
        );
      });
    },

    /**
     * [onvif]PTZ操作(パターンB:指定方向に指定秒数スクロール).
     */
    onvifPtzByScroll(xDirection, yDirection, zoomDirection, movingSecond) {
      const speedRate = 0.5; // 0.50=slow, 0.75=medium, 1.00=fast
      const xSpeed = xDirection * speedRate;
      const ySpeed = yDirection * speedRate;
      const zoomSpeed = zoomDirection * speedRate;
      const onvifProfileToken = this.camera.onvifProfileToken;

      // for debug.
      Vue.$log.debug(
        "onvifPtzByScroll() values = ",
        onvifProfileToken,
        xSpeed,
        ySpeed,
        zoomSpeed,
        movingSecond,
      );

      if (this.onvifMovingStopTimer) {
        clearTimeout(this.onvifMovingStopTimer);
      }

      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }
        let onvifApi = this;

        let apiTimeout = movingSecond * 1000;
        if (
          this.cameraModel ===
          CAMERA_MODEL.properties[CAMERA_MODEL.HV_PTZ501].model
        ) {
          apiTimeout = apiTimeout * 4;
        }
        onvifApi.continuousMove(
          {
            profileToken: onvifProfileToken,
            x: xSpeed,
            y: ySpeed,
            zoom: zoomSpeed,
            timeout: apiTimeout,
          },
          function (err, responseData, rawXml) {
            // for debug.
            Vue.$log.debug("continuousMove[xml] = ", rawXml);
          },
        );
      });

      if (
        this.cameraModel ===
          CAMERA_MODEL.properties[CAMERA_MODEL.HV_PTZ500].model ||
        this.cameraModel ===
          CAMERA_MODEL.properties[CAMERA_MODEL.HV_PTZ501].model
      ) {
        // 遅延実行でStopメソッドを一定時間後にコール
        this.onvifMovingStopTimer = setTimeout(
          this.onvifStopScroll,
          movingSecond * 1000,
        );
      }
    },

    /**
     * [onvif]PTZスクロール動作を停止.
     */
    onvifStopScroll() {
      const onvifProfileToken = this.camera.onvifProfileToken;
      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }
        let onvifApi = this;

        onvifApi.stop(
          {
            profileToken: onvifProfileToken,
            panTilt: true,
            zoom: true,
          },
          function (err, responseData, rawXml) {
            Vue.$log.debug("stop[xml] = ", rawXml);
          },
        );
      });
    },

    /**
     * [onvif]カメラホーム位置へジャンプ移動.
     */
    onvifGotoHomePosition() {
      const onvifProfileToken = this.camera.onvifProfileToken;
      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }
        let onvifApi = this;

        onvifApi.gotoHomePosition(
          {
            profileToken: onvifProfileToken,
          },
          function (err, responseData, rawXml) {
            Vue.$log.debug("gotoHomePosition[xml] = ", rawXml);
          },
        );
      });
    },

    /**
     * [onvif]カメラホーム位置のセット（現座標がホーム位置になる）.
     */
    onvifSetHomePosition() {
      const onvifProfileToken = this.camera.onvifProfileToken;
      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }
        let onvifApi = this;

        onvifApi.setHomePosition(
          {
            profileToken: onvifProfileToken,
          },
          function (err, responseData, rawXml) {
            Vue.$log.debug("setHomePosition[xml] = ", rawXml);
          },
        );
      });
    },
    onvifSetHomePositionConfirm() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "映像ホーム位置の設定",
        text: "現座標をホーム位置として設定しますか？",
        acceptText: "セットする",
        cancelText: "キャンセル",
        accept: this.onvifSetHomePosition,
      });
    },

    /**
     * (おまけ)[onvif]カメラ機器の再起動.
     *
     * (注)動画セッションの再接続のために、
     *     RaspberryPi上での「systemctl restart webcam-handler」コマンド実行が別途必要.
     *     現時点では参考程度のメソッド.
     */
    onvifReboot() {
      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }
        let onvifApi = this;

        onvifApi.systemReboot(function (err, responseData, rawXml) {
          Vue.$log.debug("systemReboot[xml] = ", rawXml);
        });
      });
    },

    /**
     * (おまけ)[onvif]解像度の変更.
     *
     * (注)解像度の変更反映には切断＋再接続が必要で、
     *     RaspberryPi上での「systemctl restart webcam-handler」コマンド実行が別途必要.
     *     現時点では参考程度のメソッド.
     */
    onvifUpdateResolution() {
      const onvifProfileToken = this.camera.onvifProfileToken;
      new Cam(this.onvifBaseParameter, function CamFunc(err) {
        if (err) {
          Vue.$log.error(err);
          return;
        }

        let targetProfile = this.profiles.find(
          (profile) => profile.$.token === onvifProfileToken,
        );
        let targetConfig = targetProfile.videoEncoderConfiguration;
        targetConfig.useCount = 1; // onvif_node-module内部で使っているので、指定が必要.
        targetConfig.H264.profile = targetConfig.H264.H264Profile; // onvif_node-module内部で使っているので、指定が必要.

        // // 解像度：低、フレームレート:デフォルト
        targetConfig.resolution = { width: 320, height: 240 };
        targetConfig.rateControl = {
          frameRateLimit: 25,
          encodingInterval: 1,
          bitrateLimit: 2147483647,
        };

        // 解像度：中、フレームレート:中
        // targetConfig.resolution = {width: 640, height: 480};
        // targetConfig.rateControl= {frameRateLimit: 15,  encodingInterval: 1, bitrateLimit: 1000};

        // // 解像度：デフォルト、フレームレート:低   // ※ SIMの速度、もしくは、ffmpegの変換サイクルに耐えられないので、非現実的。
        // targetConfig.resolution = {width: 1920, height: 1080};
        // targetConfig.rateControl= {frameRateLimit: 5,  encodingInterval: 1, bitrateLimit: 500};

        let onvifApi = this;
        onvifApi.setVideoEncoderConfiguration(
          targetConfig,
          function (err, responseData) {
            // for debug.
            Vue.$log.debug("setVideoEncoderConfiguration = ", responseData);
          },
        );
      });
    },

    /*** onvifメソッドの呼び出しサンプル ***END** */

    // zoomボタンクリック時
    zoomClicked(val) {
      if (this.mode === "history") {
        if (!isNaN(val)) {
          this.targetZoom = val;
        } else if (
          val === "plus" &&
          this.targetZoom + 1 <= this.cameraZoomMax
        ) {
          this.targetZoom++;
        } else if (
          val === "minus" &&
          this.targetZoom - 1 >= this.cameraZoomMin
        ) {
          this.targetZoom--;
        }
      } else if (this.onvifEnable && this.onvifActive) {
        if (!isNaN(val)) {
          this.targetZoom = val;

          const diffZoom = val - this.onvifCurrentZoom;
          const direction = diffZoom > 0 ? 1 : -1;
          Vue.$log.debug(
            `### zoomClicked(${val}) -> `,
            `this.onvifPtzByScroll(0, 0, ${direction}, 2)`,
          );
          this.onvifPtzByScroll(0, 0, direction, 2);
        } else if (
          val === "plus" &&
          this.targetZoom + 1 <= this.cameraZoomMax
        ) {
          this.targetZoom++;
          Vue.$log.debug(
            `### zoomClicked(${val}) -> `,
            `this.onvifPtzByScroll(0, 0, 1, 2)`,
          );
          this.onvifPtzByScroll(0, 0, 1, 2);
        } else if (
          val === "minus" &&
          this.targetZoom - 1 >= this.cameraZoomMin
        ) {
          this.targetZoom--;
          Vue.$log.debug(
            `### zoomClicked(${val}) -> `,
            `this.onvifPtzByScroll(0, 0, -1, 2)`,
          );
          this.onvifPtzByScroll(0, 0, -1, 2);
        }
      }
    },

    ptzClickEffect(val) {
      if (this.ptzClicked) {
        return;
      }

      const ptzProcessingSec = 5; // ローディング表示は固定秒数で設定

      this.ptzClicked = val;
      let el = document.getElementById(val);
      const origBackgroundColor = el.style.backgroundColor;
      el.style.backgroundColor = val.startsWith("ptz-zoom")
        ? "#237AAB"
        : "#237AAB";

      if (val.startsWith("ptz-zoom")) {
        document.getElementById("ptz-zoom-split").style.visibility = "hidden";
      }

      setTimeout(() => {
        el.style.backgroundColor = origBackgroundColor;
        if (val.startsWith("ptz-zoom")) {
          document.getElementById("ptz-zoom-split").style.visibility =
            "visible";
        }
        this.ptzClicked = null;
      }, ptzProcessingSec * 1000);

      return true;
    },

    setCarouselItemWidth() {
      const carouselWrapEl = this.$el.querySelector(".carousel-wrap");
      this.carouselItemWidth = carouselWrapEl
        ? carouselWrapEl.getBoundingClientRect().width / 8 - 4 + "px"
        : "160px";
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

button.vjs-big-play-button {
  position: absolute;
  top: 45% !important;
  display: block;
}
#camera-view {
  background-color: $aisea-white;

  .live-camera-area {
    position: relative;
  }
  .live-camera-area p {
    position: absolute;
    top: 50%;
    left: 50%;
    -ms-transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0;
    color: white;
    z-index: 5;
  }

  .live-camera-area button {
    z-index: 5;
    position: absolute;
    top: 80%;
    left: 50%;
    margin-left: 0;
    -ms-transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
  }

  .vjs-error .vjs-error-display:before {
    font-family: "Material Icons";
    content: "\E001";
    font-size: 480%;
  }

  .vjs-text {
    z-index: 1;
    font-size: 2em;
    line-height: 1.5em;
    height: 1.63332em;
    display: block;
    position: absolute;
    top: 10px;
    opacity: 1;
    transition: all 0.4s;
  }

  .forward-layer {
    background-color: #f2f2f2;
    z-index: 99;
  }

  .vue-slider-dot-handle {
    background-color: #2f80ed;
    border: 2px solid #2f80ed20;
  }

  .jump-carousel-parent {
    position: absolute;
    top: 0px;
    color: gray;
    z-index: 0;
    height: 140px;
    padding: 1rem;
    &--left {
      left: 0px;
    }
    &--right {
      right: 0px;
    }
    .jump-carousel-button {
      background-color: silver;
      opacity: 0.8;
      border-radius: 50%;
      height: 3rem;
      width: 3rem;
      z-index: 9;
      cursor: pointer;
    }
  }

  .carousel-summary-block {
    width: 2rem;
    height: 0.4rem;
    background-color: $aisea-gray;
  }

  .ptz-manage-block {
    div.ptz-ctl-box {
      width: 180px;
      height: 180px;
    }
  }

  .hykecam-camera-area {
    background-color: black;
    position: relative;
  }

  .hykecam-center {
    width: 55%;
    margin: 0 auto;
  }

  .hykecam-video {
    width: 100%;
    z-index: 10;
  }

  .loader {
    font-size: 10px;
    margin: 50px auto;
    text-indent: -9999em;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: #ffffff;
    background: -moz-linear-gradient(
      left,
      #ffffff 10%,
      rgba(255, 255, 255, 0) 42%
    );
    background: -webkit-linear-gradient(
      left,
      #ffffff 10%,
      rgba(255, 255, 255, 0) 42%
    );
    background: -o-linear-gradient(
      left,
      #ffffff 10%,
      rgba(255, 255, 255, 0) 42%
    );
    background: -ms-linear-gradient(
      left,
      #ffffff 10%,
      rgba(255, 255, 255, 0) 42%
    );
    background: linear-gradient(
      to right,
      #ffffff 10%,
      rgba(255, 255, 255, 0) 42%
    );
    position: absolute;
    top: 20%;
    left: 40%;
    -webkit-animation: load3 1.4s infinite linear;
    animation: load3 1.4s infinite linear;
    -webkit-transform: translateZ(0);
    -ms-transform: translateZ(0);
    transform: translateZ(0);
    z-index: 20;
  }
  .loader:before {
    width: 50%;
    height: 50%;
    background: #ffffff;
    border-radius: 100% 0 0 0;
    position: absolute;
    top: 0;
    left: 0;
    content: "";
  }
  .loader:after {
    background: #000000;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    content: "";
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
  @-webkit-keyframes load3 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }
  @keyframes load3 {
    0% {
      -webkit-transform: rotate(0deg);
      transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
    }
  }

  .calendar-icon {
    margin-right: 0.2rem;
  }
  .date-picker-container {
    display: inline;
  }

  .carousel-wrap {
    scroll-snap-type: x mandatory;
    white-space: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .carousel-item {
    scroll-snap-align: center;
    display: inline-block;
    white-space: normal;
  }

  .camera-view-flat-pickr {
    height: 30px;
    margin-top: 2px;
    padding: 0.3rem 0rem 0.3rem 3rem;
    width: 10rem;
    color: #48a0f4;
    border: 1px solid #48a0f4;
    background-color: #48a0f414;
  }

  .onvif-toggle-button {
    margin-top: 2px;
    height: 30px;
    border-radius: 6px;
    color: #48a0f4;
    border: 1px solid #48a0f4;
    &:hover {
      background-color: rgba(#48a0f4, 0.1);
    }
  }

  .live-icon-block {
    color: #48a0f4;
    border: 2px solid #48a0f4;
    border-radius: 4px;
    box-shadow: 0 0 10px 0 #48a0f480;
  }

  .con-vs-loading {
    z-index: 999;
  }

  .onvif-waiting {
    pointer-events: none;
    text-align: center;
    position: absolute;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 8px solid #e2e2e2;
    border-right-color: transparent;
    -webkit-animation: spin 1s infinite linear;
    -moz-animation: spin 1s infinite linear;
    -ms-animation: spin 1s infinite linear;
  }

  @-webkit-keyframes spin {
    0% {
      -webkit-transform: rotate(0deg);
      opacity: 1;
    }
    50% {
      -webkit-transform: rotate(180deg);
      opacity: 1;
    }
    100% {
      -webkit-transform: rotate(360deg);
      opacity: 1;
    }
  }
  @-moz-keyframes spin {
    0% {
      -moz-transform: rotate(0deg);
      opacity: 1;
    }
    50% {
      -moz-transform: rotate(180deg);
      opacity: 1;
    }
    100% {
      -moz-transform: rotate(360deg);
      opacity: 1;
    }
  }
  @-ms-keyframes spin {
    0% {
      -ms-transform: rotate(0deg);
      opacity: 1;
    }
    50% {
      -ms-transform: rotate(180deg);
      opacity: 1;
    }
    100% {
      -ms-transform: rotate(360deg);
      opacity: 1;
    }
  }
}

progress {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border-radius: 2px;
  width: 100%;
  height: 8.4px;
  pointer-events: none;
  position: absolute;
  top: 0;
}

progress::-webkit-progress-bar {
  background-color: #474545;
  border-radius: 2px;
}

progress::-webkit-progress-value {
  background-color: #474545;
  border-radius: 2px;
}

.seek {
  position: absolute;
  top: 0;
  width: 100%;
  cursor: pointer;
  margin: 0;
}

.seek:hover + .seek-tooltip {
  display: block;
}

.seek-tooltip {
  display: none;
  position: absolute;
  top: -50px;
  margin-left: -20px;
  font-size: 12px;
  padding: 3px;
  content: attr(data-title);
  font-weight: bold;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.6);
}

.bottom-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.6rem;
}

.left-controls {
  display: flex;
  align-items: center;
  color: #fff;
}

.right-controls {
  display: flex;
  align-items: center;
  color: #fff;
}

.volume-controls {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.volume-controls input {
  width: 100px;
  opacity: 1;
  transition: all 0.4s ease;
}

.volume-controls:hover input,
.volume-controls input:focus {
  width: 100px;
  opacity: 1;
}

.fullscreen-button {
  margin-right: 0;
}

.playback-animation {
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 50%;
  margin-left: -40px;
  margin-top: -40px;
  width: 80px;
  height: 80px;
  border-radius: 80px;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
}

input[type="range"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 8.4px;
  background: transparent;
  cursor: pointer;
}

input[type="range"]:focus {
  outline: none;
}

input[type="range"]::-webkit-slider-runnable-track {
  width: 100%;
  cursor: pointer;
  border-radius: 1.3px;
  -webkit-appearance: none;
  transition: all 0.4s ease;
}

input[type="range"]::-webkit-slider-thumb {
  height: 16px;
  width: 16px;
  border-radius: 16px;
  background: #7367f0;
  cursor: pointer;
  -webkit-appearance: none;
  margin-left: -1px;
}

input[type="range"]:focus::-webkit-slider-runnable-track {
  background: transparent;
}

input[type="range"].volume {
  height: 5px;
  background-color: #fff;
}

input[type="range"].volume::-webkit-slider-runnable-track {
  background-color: transparent;
}

input[type="range"].volume::-webkit-slider-thumb {
  margin-left: 0;
  height: 14px;
  width: 14px;
  background: #fff;
}

input[type="range"]::-moz-range-track {
  width: 100%;
  height: 8.4px;
  cursor: pointer;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 1.3px;
}

input[type="range"]::-moz-range-thumb {
  height: 14px;
  width: 14px;
  border-radius: 50px;
  border: 1px solid #7367f0;
  background: #7367f0;
  cursor: pointer;
  margin-top: 5px;
}

input[type="range"]:focus::-moz-range-track {
  outline: none;
}

input[type="range"].volume::-moz-range-thumb {
  border: 1px solid #fff;
  background: #fff;
}

.play-button {
  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin-right: 1rem;
  }
}

.volume-button {
  color: $aisea-white;
  margin: 0 0.4rem;
}

.onvif-control-button {
  &:hover {
    background-color: #6ab9e5 !important;
  }
}

.onvif-zoom-button {
  &:hover {
    background-color: #5c5c5c !important;
  }
}

[dir] .flatpickr-calendar .flatpickr-day.endRange.prevMonthDay,
[dir] .flatpickr-calendar .flatpickr-day.selected.nextMonthDay,
[dir] .flatpickr-calendar .flatpickr-day.selected {
  background-color: #ffffff !important;
  border-color: darkgray !important;
  color: black !important;
}
[dir] .flatpickr-calendar .flatpickr-day.selected:hover {
  background-color: #2f80eda0 !important;
  border-color: #2f80edc0 !important;
  color: white !important;
}

#custom-breadcrumb-title {
  display: flex;
  align-items: center;
  position: absolute;
  padding: 0;
  margin: 0;
}

#ptz-manage-block-area {
  .title-loading {
    font-size: 6px !important;
  }
}
</style>
